import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { browserHistory } from 'react-router';
import moment from 'moment';
import _ from 'lodash';

import useUpdateEffect from '../../../hooks/useUpdateEffect';
import {
	addToPartsCart,
	ajaxRequestCompleted,
	ajaxRequestInitiated,
	saveAvailabilityToCart,
	savePartsSearchResult,
	savePricesToCart,
	saveToCart,
	updateCartOrderPoints
} from '../../../redux/actions';
import { DATE_FORMAT, DATE_TIME_FORMAT } from '../../common/messageConstants';
import { shippingMethods } from '../../common/partsNetConstants';

const PartsNetSearchResult = props => {
	const {
		user: { userDetails, selectedAccount, selectedPriceSource, selectedAddress },
		cart,
		product: { partsSearchResult },
		loading
	} = useSelector(({ user, cart, product, loading }) => ({ user, cart, product, loading }));
	const [sortBy, setSortBy] = useState('ITEM_NUMBER_A_TO_Z');
	const dispatch = useDispatch();
	const [showImages, setShowImages] = useState(true);

	const partsSearchResultItems = partsSearchResult && partsSearchResult.items;

	useEffect(() => {
		if (
			props.params.searchKeyword &&
			props.params.searchKeyword.trim() &&
			(!partsSearchResult || (partsSearchResult && !partsSearchResult.inProgress && !partsSearchResult.items))
		) {
			dispatch(
				savePartsSearchResult({
					inProgress: true
				})
			);
			props.getItemDetails(
				{
					items: props.params.searchKeyword
						.split(/[^\d\w-]/g)
						.filter(keyword => keyword)
						.map(itemNumber => ({ itemNumber })),
					agreementNumber: selectedPriceSource.AGREEMENT_NUMBER
				},
				searchResult => {
					dispatch(
						savePartsSearchResult({
							inProgress: false,
							items: _.uniqBy(
								searchResult.items.filter(item => !item.isInvalid),
								'itemNumber'
							)
						})
					);
				},
				{
					searchContext: 'PNET',
					include: {
						pim: true,
						availability: true,
						orderability: true,
						pricing: true,
						itemSearch: true
					},
					skipPOKCheck: true,
					showLoader: true
				}
			);
		}

		return () => dispatch(ajaxRequestCompleted());
	}, []);

	useEffect(() => {
		if (partsSearchResult && partsSearchResult.inProgress) {
			if (!loading || !loading.isFetching) {
				dispatch(ajaxRequestInitiated());
			}
		} else {
			dispatch(ajaxRequestCompleted());
		}
	}, [partsSearchResult]);

	useUpdateEffect(() => {
		if (selectedAddress && partsSearchResult && partsSearchResult.items && partsSearchResult.items.length) {
			dispatch(
				savePartsSearchResult({
					inProgress: true
				})
			);

			props.getItemDetails(
				{
					items: partsSearchResult.items.map(({ itemNumber }) => ({ itemNumber })),
					agreementNumber: selectedPriceSource.AGREEMENT_NUMBER
				},
				searchResult => {
					dispatch(
						savePartsSearchResult({
							inProgress: false,
							items: partsSearchResult.items.map(item => {
								const foundItem = searchResult.items.find(searchResultItem => searchResultItem.itemNumber === item.itemNumber) || {};

								const newItem = { ...item };
								newItem.availabilityData = foundItem.availabilityData;
								newItem.orderabilityData = foundItem.orderabilityData;
								newItem.isRestricted = foundItem.isRestricted;
								return newItem;
							})
						})
					);
				},
				{
					searchContext: 'PNET',
					include: {
						availability: true,
						orderability: true
					},
					showLoader: true
				}
			);
		}
	}, [selectedAddress]);

	useEffect(() => {
		if (partsSearchResult && partsSearchResult.keyword && partsSearchResult.keyword.trim() && partsSearchResult.keyword.trim() !== props.params.searchKeyword) {
			browserHistory.push(`/search-result/product-number/${partsSearchResult.keyword}`);
		}
	}, [partsSearchResult]);

	const toggleImages = e => {
		e.preventDefault();
		setShowImages(showImages => !showImages);
	};

	const handleSort = e => {
		setSortBy(e.target.value);
	};

	const handleItemInfo = (item, e) => {
		e.preventDefault();
		!item.isNLA && item.itemData && item.itemData.imagePath && browserHistory.push(`/product-specs/product-number/${item.itemNumber}`);
	};

	const handleAddToCart = item => {
		let cartPayload = cart.cart && cart.cart.responseObject;

		if (!cartPayload || !cartPayload.cartItems || !cartPayload.cartItems.length) {
			cartPayload = {
				accountNumber: selectedAccount.ACCOUNTNUMBER,
				createdDate: moment().format('MM/DD/YYYY'),
				updatedDate: moment().format('MM/DD/YYYY'),
				userId: userDetails.sso,
				orderType: 'R',
				requestedShipDate: moment().format('MM/DD/YYYY'),
				shipMethod: shippingMethods[props.isInternationalPartsCustomer ? 'international' : 'domestic']['regular'][0].displayName,
				shipSet: '',
				// freightForwarder: freightForwarder || undefined,
				cartItems: [
					{
						id: item.id,
						item: item.itemNumber,
						quantity: item.isPOKInstall ? item.pimData.geego_pok_ord_qty : 1,
						itemDescription: item.pimData && item.pimData.item_description,
						agreementNumber: `${item.agreementNumber || selectedPriceSource.AGREEMENT_NUMBER}`,
						createdDate: moment().format(DATE_TIME_FORMAT),
						updatedDate: moment().format(DATE_TIME_FORMAT),
						poNumber: '',
						parentItem: item.originalItemNumber,
						buyerLineNo: item.id,
						imagePath: (item.itemData && item.itemData.imagePath) || ''
					}
				]
			};
		} else {
			cartPayload = {
				...cartPayload,
				cartItems: [
					...cartPayload.cartItems,
					{
						id: item.id,
						item: item.itemNumber,
						quantity: item.isPOKInstall ? item.pimData.geego_pok_ord_qty : 1,
						itemDescription: item.pimData && item.pimData.item_description,
						agreementNumber: `${item.agreementNumber || selectedPriceSource.AGREEMENT_NUMBER}`,
						createdDate: moment().format(DATE_TIME_FORMAT),
						updatedDate: moment().format(DATE_TIME_FORMAT),
						poNumber: '',
						parentItem: item.originalItemNumber,
						buyerLineNo: item.id,
						imagePath: (item.itemData && item.itemData.imagePath) || ''
					}
				]
			};
		}

		dispatch(
			addToPartsCart(cartPayload, cartResponse => {
				if (cartResponse.status && cartResponse.responseObject) {
					dispatch(saveToCart(cartResponse));

					const cartPimData = JSON.parse(JSON.stringify(cart.cartPimData ? cart.cartPimData.pimData : [])) || [];
					const cartAvailabilityData = cart.availability ? [...cart.availability] : [];
					const cartPricingData = [];

					if (item.pimData && !cartPimData.find(pimItem => pimItem.item_number === item.itemNumber)) {
						cartPimData.push(item.pimData);
					}

					if (item.availabilityData) {
						cartAvailabilityData.push(item.availabilityData);
					}

					if (item.pricingData) {
						if (!cartPricingData.find(pricingItem => pricingItem.item_number === item.itemNumber)) {
							cartPricingData.push(item.pricingData);
						}
					}

					dispatch(updateCartOrderPoints({ pimData: cartPimData }));
					dispatch(saveAvailabilityToCart({ responseObject: cartAvailabilityData }));
					if (cartPricingData.length) {
						dispatch(savePricesToCart({ responseObject: { items: cartPricingData } }, true));
					}
				} else {
					browserHistory.push('/error');
				}
			})
		);
	};

	const itemInCart = useMemo(() => {
		if (partsSearchResultItems && cart.cart && cart.cart.responseObject && cart.cart.responseObject.cartItems && cart.cart.responseObject.cartItems.length) {
			return partsSearchResultItems.reduce((acc, cur) => {
				acc[cur.itemNumber] = cart.cart.responseObject.cartItems.some(cartItem => cartItem.item === cur.itemNumber);
				return acc;
			}, {});
		}
		return {};
	}, [partsSearchResultItems, cart]);

	const sortedPartsSearchResultItems = useMemo(() => {
		if (partsSearchResultItems) {
			switch (sortBy) {
				case 'ITEM_NUMBER_A_TO_Z':
					return _.orderBy(partsSearchResultItems, 'itemNumber');
				case 'PRICE_HIGH_TO_LOW':
					return _.orderBy(
						partsSearchResultItems,
						item => +(item.pricingData && item.pricingData.records && item.pricingData.records.length && item.pricingData.records[0].adjustedUnitPrice),
						'desc'
					);
				case 'PRICE_LOW_TO_HIGH':
					return _.orderBy(
						partsSearchResultItems,
						item => +(item.pricingData && item.pricingData.records && item.pricingData.records.length && item.pricingData.records[0].adjustedUnitPrice)
					);
				default:
					return partsSearchResultItems;
			}
		}
	}, [partsSearchResultItems, sortBy]);

	return (
		<div className="pNetSearchResult">
			<h2>
				<span style={{ fontWeight: 'normal' }}>You searched:</span>&nbsp;
				{props.params.searchKeyword ? props.params.searchKeyword.trim().replaceAll(/( )+/g, ', ').toUpperCase() : ''}
			</h2>
			<div className="productResultsTableForm withNoBeforeUnloadBehavior" name="productResultsTableForm" id="productResultsTableForm">
				<div className="captionAboveTable productResultsTableHeader">
					<div className="inner">
						<div className="resultCount">
							<span className="resultCount">{((partsSearchResult && partsSearchResult.items) || []).length} Items</span> match your selections.
						</div>
						<div className="sortBy">
							<label>
								Sort by:&nbsp;
								<select id="sortBy" name="sortBy" className="sortBy font12" onChange={handleSort}>
									<option value="ITEM_NUMBER_A_TO_Z">Item Number: A-Z</option>
									<option value="PRICE_HIGH_TO_LOW">Price: High to Low</option>
									<option value="PRICE_LOW_TO_HIGH">Price: Low to High</option>
								</select>
							</label>
						</div>
					</div>
				</div>
				<table className={'dataTable productResultsTable partsSearchResultsTable'}>
					<thead>
						<tr>
							{showImages && (
								<th className="photo">
									<a className="bold" onClick={toggleImages} href="">
										Hide Photos
									</a>
								</th>
							)}
							<th className="productInfo">
								{!showImages && (
									<a className="bold" onClick={toggleImages} href="">
										Show Photos
									</a>
								)}
							</th>
							<th className="priceColumn msrp"></th>
							<th className="priceColumn price">Book Price</th>
							<th className="priceColumn netPrice">Selling Price</th>
							<th>Volume Discount Eligible</th>
							<th className="cart"></th>
						</tr>
					</thead>
					{!sortedPartsSearchResultItems || !sortedPartsSearchResultItems.length ? (
						<tbody>
							<tr>
								<td className="text-center" colSpan={7}>
									No results found.
								</td>
							</tr>
						</tbody>
					) : (
						sortedPartsSearchResultItems.map(item => {
							const { pimData: itemPimData, availabilityData: itemAvailabilityData, pricingData: itemPricingData } = item;

							const isQuantityNotAvailableOnRequestedDate = itemAvailabilityData && itemAvailabilityData.availableQuantity >= 1410065408;
							const itemData = itemAvailabilityData
								? {
										availableQuantity: itemAvailabilityData.availableQuantity,
										warehouseCode: itemAvailabilityData.sourceOrganizationCode && itemAvailabilityData.sourceOrganizationCode.split(':').at(-1),
										eta: itemAvailabilityData.arrivalDate,
										availabilityMessage:
											(isQuantityNotAvailableOnRequestedDate && 'BACKORDER') ||
											(itemAvailabilityData.availableQuantity && itemAvailabilityData.availableQuantity < item.requestedQuantity && 'PARTIAL') ||
											'',
										errorCode: itemAvailabilityData.errorCode
								  }
								: {};

							let unitPrice = 0,
								adjustedUnitPrice = 0;
							if (itemPricingData && itemPricingData.records && itemPricingData.records.length && itemPricingData.records[0].unitPrice) {
								unitPrice = itemPricingData.records[0].unitPrice;
								adjustedUnitPrice = itemPricingData.records[0].adjustedUnitPrice || 0;
							}

							const showItemDetails = !item.isRestricted && !item.isNLA && !!unitPrice;

							return (
								<tbody key={item.id}>
									<tr>
										{showImages && (
											<td className="photo">
												<a
													href=""
													onClick={handleItemInfo.bind(this, item)}
													style={{
														cursor: !item.isNLA && item.itemData && item.itemData.imagePath ? 'intial' : 'default',
														color: 'inherit',
														textDecoration: 'none'
													}}
												>
													{item.itemData && item.itemData.imagePath ? (
														<img src={item.itemData.imagePath || ''} alt={item.itemNumber} width={96} height={100} />
													) : (
														<span className="flex items-center text-center bold" style={{ backgroundColor: '#eaeaea' }}>
															NO IMAGE AVAILABLE
														</span>
													)}
												</a>
											</td>
										)}
										<td className="productInfo">
											<div className="skuDiv">
												<a
													href=""
													onClick={handleItemInfo.bind(this, item)}
													style={{ cursor: !item.isNLA && item.itemData && item.itemData.imagePath ? 'intial' : 'default' }}
												>
													{item.itemNumber}
												</a>
											</div>
											{item.originalItemNumber && (
												<p style={{ color: '#7a7a7a' }}>
													Supersedes <strong style={{ color: '#1a63a2' }}>{item.originalItemNumber}</strong>
												</p>
											)}
											<div className="productNameDiv">{item.pimData && item.pimData.item_description}</div>
										</td>
										<td style={{ width: '25%' }}>
											{showItemDetails && item.availabilityData && (
												<table className="productStatusTable">
													<thead>
														<tr>
															<th className="text-center">Available Qty</th>
															<th className="text-center">ETA</th>
															<th className="text-center">Whse</th>
															{item.isPOKInstall && <th className="text-center">Box Qty</th>}
														</tr>
													</thead>
													<tbody>
														<tr>
															<td
																className={`text-center bold${isQuantityNotAvailableOnRequestedDate && !itemData.eta ? ' red' : ''}`}
																style={{ width: '30%' }}
															>
																<React.Fragment>
																	{isQuantityNotAvailableOnRequestedDate ? 0 : itemData.availableQuantity}
																	{itemData.availabilityMessage && (
																		<strong className="block" style={{ fontSize: '.7em', marginTop: '.25rem', color: '#7a7a7a' }}>
																			{itemData.availabilityMessage}
																		</strong>
																	)}
																</React.Fragment>
															</td>
															<td className="text-center">
																{isQuantityNotAvailableOnRequestedDate && itemData.eta && moment(itemData.eta).format(DATE_FORMAT)}
															</td>
															<td className="text-center">{itemData.warehouseCode}</td>
															{item.isPOKInstall && <td className="text-center">{itemPimData.geego_pok_ord_qty}</td>}
														</tr>
													</tbody>
												</table>
											)}
										</td>
										<td className="priceColumn price" style={{ width: '8%' }}>
											{showItemDetails && props.formatMoney(parseFloat(unitPrice).toFixed(2), 0, '.', ',', false, '$')}
										</td>
										<td className="priceColumn price">
											{showItemDetails && props.formatMoney(parseFloat(adjustedUnitPrice).toFixed(2), 0, '.', ',', false, '$')}
										</td>
										<td>{showItemDetails && (item.isVolumeDiscountEligible ? 'Yes' : 'No')}</td>
										<td className="cart text-right">
											<p className="addProductBtn">
												<button
													className={`blockButton ${item.isNLA || !unitPrice || itemInCart[item.itemNumber] ? ' grayButton' : ' addToCartButton'}`}
													onClick={handleAddToCart.bind(this, item)}
													disabled={item.isRestricted || item.isNLA || !unitPrice || itemInCart[item.itemNumber]}
												>
													{item.isRestricted
														? `Restricted${item.restrictionReason ? ` - ${item.restrictionReason}` : ''}`
														: item.isNLA
														? 'NLA'
														: !unitPrice
														? 'Not in Price List'
														: itemInCart[item.itemNumber]
														? 'In Cart'
														: 'Add To Cart'}
												</button>
											</p>
										</td>
									</tr>
								</tbody>
							);
						})
					)}
				</table>
			</div>
		</div>
	);
};

export default PartsNetSearchResult;



// WEBPACK FOOTER //
// ./src/App/components/PostLogin/PartsNetSearchResult/index.jsx